import { createAsyncThunk } from '@reduxjs/toolkit';

import { editorTypeToFilterPlatform } from '../../util/getEditorTypeFilter';
import { logger } from '../../util/logger';
import { renderModel } from '../../util/renderModel';
import { httpClient } from '../httpClient';
import { EDITOR_TYPE } from '../messageCreators';
import { TRootState } from '../reducers';
import { homepageSlice } from '../reducers/homepage';
import { ISiteItem } from '../reducers/sites';

const { clientTopology } = renderModel.get();
const isEditor3 = renderModel.isEditor3();
const isStudioEditor = renderModel.isStudioEditor();

interface ILastInteractedSiteResponse {
  site?: {
    metaSiteId: string;
    name: string;
    viewerUrl: string;
    published: boolean;
    dateUpdated: string;
    ownerId: string;
    premiumAssigned: boolean;
    editorType?: string;
  };
}

const sortSitesByDate = (a: ISiteItem, b: ISiteItem): number => {
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
};

const convertEditorType = (type: string | undefined): EDITOR_TYPE => {
  if (type && Object.values(EDITOR_TYPE).includes(type as EDITOR_TYPE)) {
    return type as EDITOR_TYPE;
  }
  return EDITOR_TYPE.UNKNOWN_TYPE;
};

const mapSiteEntry =
  () =>
  (site: any): ISiteItem => ({
    id: site.metaSiteId,
    name: site.siteProperties?.siteDisplayName ?? site.name,
    url: site.viewerUrl ?? '',
    updatedAt: String(site.dateUpdated ?? ''),
    thumbnail: `https://www.wix.com/site-thumbnail/${site.metaSiteId}?preset=site-details`,
    editorType: convertEditorType(site.editor?.editorType),
  });

export const fetchSitesApi = async (userId: string, term?: string): Promise<ISiteItem[]> => {
  try {
    const { data } = await httpClient.getInstance().get(clientTopology.sitesSearchUrl, { params: { term } });

    if (!Array.isArray(data.sites)) {
      throw new Error('Invalid response format');
    }

    const mapSites = mapSiteEntry();
    return data.sites.map(mapSites).sort(sortSitesByDate);
  } catch (error) {
    logger.error({
      error,
      message: 'Failed to fetch user sites',
      callParams: { userId },
    });
    return Promise.reject(error);
  }
};

const fetchLastInteractedSite = (): Promise<ISiteItem | undefined> => {
  return httpClient
    .getInstance()
    .get(clientTopology.lastInteractedSiteUrl)
    .then(({ data }) => {
      const response = data as ILastInteractedSiteResponse;
      if (!response.site) {
        return undefined;
      }

      const site = response.site;
      return {
        id: site.metaSiteId,
        name: site.name,
        url: site.viewerUrl,
        updatedAt: site.dateUpdated,
        thumbnail: `https://www.wix.com/site-thumbnail/${site.metaSiteId}?preset=site-details`,
        editorType: convertEditorType(site.editorType),
      };
    })
    .catch((error) => {
      logger.error({
        error,
        message: 'Failed to fetch last interacted site',
      });
      return undefined;
    });
};

interface FetchSitesResult {
  sites: ISiteItem[];
  selectedSite: ISiteItem | null;
}

export const fetchSites = createAsyncThunk<FetchSitesResult, string, { state: TRootState }>(
  'sites/fetchSites',
  async (userId: string, { dispatch, getState }) => {
    const [sites, lastInteractedSite] = await Promise.all([fetchSitesApi(userId), fetchLastInteractedSite()]);

    let selectedSite: ISiteItem | null = null;

    const copilotMetaSiteId = getState().sites?.copilotMetaSiteId;
    if (copilotMetaSiteId) {
      selectedSite = sites.find((site) => site.id === copilotMetaSiteId) || null;
    }

    if (!selectedSite && lastInteractedSite) {
      selectedSite = sites.find((site) => site.id === lastInteractedSite.id) || null;

      if (!selectedSite) {
        selectedSite = lastInteractedSite;
        sites.push(lastInteractedSite);
        sites.sort(sortSitesByDate);
      }
    }

    if (!selectedSite && sites.length > 0) {
      selectedSite = sites[0];
    }

    if (selectedSite?.editorType) {
      const editorTypeFilter = editorTypeToFilterPlatform[selectedSite.editorType];
      if (editorTypeFilter) {
        dispatch(homepageSlice.actions.setEditorType(editorTypeFilter));
      }
    }

    return { sites, selectedSite };
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      return !state.sites?.hasFetched && !state.sites?.isLoading && !isEditor3 && !isStudioEditor;
    },
  },
);
